import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Zap, Maximize2, Bell, KeyRound, Download, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { isInstallOfferBlocked } from '@/pwa/pwaEnvironment';
import { getBrowser, isMobile, isStandalone } from '@/pwa/installContext';
import { claimPrompt, releasePrompt } from '@/pwa/promptCoordinator';
import { hasInstalledOnPlatform, recordStandaloneOpen, trackInstallEvent } from '@/pwa/installTracking';
import { InstallSteps } from '@/components/pwa/InstallSteps';
import { InstallGuideDialog } from '@/components/pwa/InstallGuideDialog';
import { PWA_APP } from '@/pwa/appTargets';

/** "Continuar no navegador" vale só até recarregar a página (memória, sem armazenamento). */
try { localStorage.removeItem('autofluxo_install_screen_skip_until'); } catch { /* ignore */ }
try { sessionStorage.removeItem('autofluxo_install_screen_skip_session'); } catch { /* ignore */ }
let skippedThisLoad = false;
const sessionSkipped = () => skippedThisLoad;
const skipForSession = () => { skippedThisLoad = true; };
const HINT_KEY = 'autofluxo_installed_hint_closed_until';
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Rotas onde a tela/aviso nunca aparecem. */
const EXCLUDED = [
  '/login', '/esqueci-senha', '/redefinir-senha', '/auth', '/~oauth', '/confirmar-email',
  '/cadastro-corretor', '/corretor', '/escolher-ambiente', '/instalar', '/politica-de-privacidade',
  '/crm/chat', '/vendas/nova', '/negocios/nova', '/diagnostico',
];
const isExcluded = (p: string) => p === '/' || EXCLUDED.some((x) => p === x || p.startsWith(`${x}/`));

const until = (k: string) => { try { return Number(localStorage.getItem(k) || 0); } catch { return 0; } };
const setUntil = (k: string) => { try { localStorage.setItem(k, String(Date.now() + WEEK_MS)); } catch { /* ignore */ } };

/**
 * Celular pelo navegador: tela cheia "Instale o AutoFluxo" ou, se a conta já usa o app
 * instalado neste tipo de aparelho, um aviso discreto. No app instalado registra o acesso.
 */
export function MobileInstallFlow() {
  const { user } = useSupabaseAuth();
  const userId = user?.id;
  const { pathname } = useLocation();
  const { canPrompt, promptInstall } = usePwaInstall();
  const [mode, setMode] = useState<'none' | 'screen' | 'hint'>('none');
  const [guideOpen, setGuideOpen] = useState(false);
  const recordedRef = useRef(false);
  const decidedRef = useRef<string | null>(null);
  const excluded = isExcluded(pathname.toLowerCase());
  const browser = getBrowser();

  // App instalado: registra o acesso uma vez por sessão.
  useEffect(() => {
    if (!userId || recordedRef.current || !isStandalone() || isInstallOfferBlocked()) return;
    recordedRef.current = true;
    void recordStandaloneOpen();
  }, [userId]);

  useEffect(() => {
    if (excluded && mode !== 'none') {
      releasePrompt(mode === 'screen' ? 'install-screen' : 'installed-hint');
      setMode('none');
    }
  }, [excluded, mode]);

  useEffect(() => {
    if (!userId || excluded || mode !== 'none' || decidedRef.current === userId) return;
    if (isInstallOfferBlocked() || !isMobile() || isStandalone()) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const installed = await hasInstalledOnPlatform(userId);
      if (cancelled) return;
      decidedRef.current = userId;
      if (installed) {
        if (until(HINT_KEY) > Date.now() || !claimPrompt('installed-hint')) return;
        setMode('hint');
        void trackInstallEvent(userId, 'installed_hint_shown');
        return;
      }
      if (sessionSkipped() || !claimPrompt('install-screen')) return;
      setMode('screen');
      void trackInstallEvent(userId, 'screen_shown');
    }, 1500);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [userId, excluded, mode]);

  const close = () => {
    releasePrompt(mode === 'screen' ? 'install-screen' : 'installed-hint');
    setMode('none');
  };

  const onInstall = async () => {
    void trackInstallEvent(userId, 'install_click');
    if (canPrompt && browser === 'android') {
      const outcome = await promptInstall();
      if (outcome === 'accepted') { void trackInstallEvent(userId, 'install_accepted'); close(); }
      else if (outcome === 'dismissed') void trackInstallEvent(userId, 'install_dismissed');
      else document.getElementById('install-steps')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    document.getElementById('install-steps')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (mode === 'hint') {
    return (
      <>
        <div className="fixed inset-x-3 z-[300] mx-auto max-w-md rounded-lg border border-border bg-card p-3 shadow-lg"
             style={{ bottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}>
          <div className="flex items-start gap-3">
            <img src={PWA_APP.icon} alt="" aria-hidden className="h-9 w-9 shrink-0 rounded-lg" />
            <p className="min-w-0 flex-1 text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">Você já tem o AutoFluxo instalado.</span>{' '}
              Abra pelo ícone na tela inicial para receber notificações.
              <button type="button" className="ml-1 font-semibold text-primary underline" onClick={() => setGuideOpen(true)}>
                Ver passo a passo
              </button>
            </p>
            <button type="button" aria-label="Fechar" className="text-muted-foreground hover:text-foreground"
                    onClick={() => { setUntil(HINT_KEY); close(); }}>
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <InstallGuideDialog open={guideOpen} onClose={() => setGuideOpen(false)} />
      </>
    );
  }

  if (mode !== 'screen') return null;

  return (
    <div className="dark fixed inset-0 z-[390] overflow-y-auto bg-background text-foreground"
         style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
         role="dialog" aria-modal="true" aria-label="Instale o AutoFluxo">
      <div className="mx-auto flex min-h-full max-w-md flex-col px-5 py-8">
        <div className="flex flex-col items-center text-center animate-fade-in">
          <div className="relative">
            <span className="absolute inset-0 animate-ping rounded-3xl bg-primary/25" />
            <img src={PWA_APP.icon} alt="AutoFluxo" className="relative h-20 w-20 rounded-3xl shadow-lg" />
          </div>
          <h1 className="mt-5 text-2xl font-bold">Instale o AutoFluxo</h1>
          <p className="mt-1 text-sm text-muted-foreground">Use como aplicativo no seu celular.</p>
        </div>

        <ul className="mt-6 space-y-2 text-sm">
          <li className="flex items-center gap-3"><Zap className="h-4 w-4 text-primary" />Abre mais rápido, direto do ícone.</li>
          <li className="flex items-center gap-3"><Maximize2 className="h-4 w-4 text-primary" />Tela cheia, sem a barra do navegador.</li>
          <li className="flex items-center gap-3"><Bell className="h-4 w-4 text-primary" />Receba notificações de vendas, tarefas e chamados.</li>
        </ul>

        {browser !== 'in-app' && (
          <Button size="lg" className="mt-6 h-12 w-full text-base font-semibold" onClick={onInstall}>
            <Download className="mr-2 h-5 w-5" />Instalar o AutoFluxo
          </Button>
        )}

        <div id="install-steps" className="mt-6">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {browser === 'in-app' ? 'Primeiro, abra no Safari ou Chrome' : 'Como instalar'}
          </p>
          <InstallSteps browser={browser} />
        </div>

        <p className="mt-5 flex items-start gap-2 rounded-lg bg-muted/60 p-3 text-xs text-muted-foreground">
          <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          Depois de instalar, abra pelo ícone e entre com seu e-mail e senha no app.
        </p>

        <button type="button" className="mx-auto mt-6 text-xs text-muted-foreground underline hover:text-foreground"
                onClick={() => { skipForSession(); void trackInstallEvent(userId, 'continue_browser'); close(); }}>
          Continuar no navegador por enquanto
        </button>
      </div>
    </div>
  );
}
